import { Box, Grid, Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';

import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import styled from 'styled-components';
import { convert24HourTo12Hour } from '@alluradirect/react';

import { CheckoutSectionTitle } from 'components/views/pages/Checkout/components/CheckoutSectionTitle';
import { BookingCheckoutResponse } from 'components/views/pages/Checkout/hooks/useCheckout';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import CircleIcon from '@mui/icons-material/Circle';

const StyledPolicyItem = styled(Box)`
  display: flex;
  align-items: center;

  & svg {
    margin-right: ${props => props.theme.spacing(2)};
    font-size: 18px;
  }

  &:not(:first-child) {
    margin-top: ${props => props.theme.spacing(1)};
  }
`;

const StyledCircleIcon = styled(CircleIcon)`
  font-size: 10px !important;
  margin-right: ${props => props.theme.spacing(2.5)} !important;
  margin-left: ${props => props.theme.spacing(0.5)} !important;
`;

interface HouseRulesProps {
  label: string;
  value: string | number;
  isAllowed?: boolean;
  isDisallowed?: boolean;
  isLoading?: boolean;
}

const HouseRulesItem = ({ label, value, isAllowed, isDisallowed, isLoading = false }: HouseRulesProps) => {
  return (
    <LoadingSkeleton isLoading={isLoading}>
      <StyledPolicyItem>
        {!isAllowed && !isDisallowed ? <StyledCircleIcon /> : null}
        {isAllowed && <CheckIcon color="success" />}
        {isDisallowed && <BlockIcon color="error" />}
        <Typography variant="body1">
          {label} <strong>{value}</strong>
        </Typography>
      </StyledPolicyItem>
    </LoadingSkeleton>
  );
};

interface Props {
  checkoutData: BookingCheckoutResponse | undefined;
}

export const CheckoutHouseRules = ({ checkoutData }: Props) => {
  const isLoading = !checkoutData;

  const checkInTime = checkoutData ? convert24HourTo12Hour(checkoutData.BOOKING.PROPERTY.RULES.CHECK_IN) : '';
  const checkOutTime = checkoutData ? convert24HourTo12Hour(checkoutData.BOOKING.PROPERTY.RULES.CHECK_OUT) : '';
  const quietTime = checkoutData ? convert24HourTo12Hour(checkoutData.BOOKING.PROPERTY.RULES.QUIET_TIME) : '';
  const maxGuests = checkoutData?.BOOKING.PROPERTY.RULES.MAX_GUESTS ?? 0;
  const minAge = checkoutData?.BOOKING.PROPERTY.RULES.MIN_AGE_TO_BOOK ?? 0;

  const isWheelchairAccessible = checkoutData
    ? checkoutData.BOOKING.PROPERTY.SUITABILITIES.WHEELCHAIR_ACCESSIBLE.IS_SUITABLE
    : false;
  const isSmokingAllowed = checkoutData ? checkoutData.BOOKING.PROPERTY.SUITABILITIES.SMOKING.IS_SUITABLE : false;
  const isPartiesEventsAllowed = checkoutData
    ? checkoutData.BOOKING.PROPERTY.SUITABILITIES.PARTIES_EVENTS.IS_SUITABLE
    : false;
  const isPetFriendly = checkoutData ? checkoutData.BOOKING.PROPERTY.IS_PET_FRIENDLY : false;
  const allowsLongTermRentals = checkoutData
    ? checkoutData.BOOKING.PROPERTY.SUITABILITIES.LONG_TERM_RENTALS.IS_SUITABLE
    : false;
  const allowsTravelResellers = checkoutData
    ? checkoutData.BOOKING.PROPERTY.SUITABILITIES.TRAVEL_RESELLERS.IS_SUITABLE
    : false;

  return (
    <>
      <Grid item xs={12}>
        <CheckoutSectionTitle>General Rules</CheckoutSectionTitle>

        <HouseRulesItem label={'Check in time is after'} value={checkInTime} isLoading={isLoading} />
        <HouseRulesItem label={'Check out time is before'} value={checkOutTime} isLoading={isLoading} />
        <HouseRulesItem label={'Quiet time begins at'} value={quietTime} isLoading={isLoading} />
        <HouseRulesItem label={'Maximum guests is'} value={maxGuests} isLoading={isLoading} />
        <HouseRulesItem label={'Minimum age to rent is'} value={minAge} isLoading={isLoading} />

        {(isPetFriendly ||
          isWheelchairAccessible ||
          isSmokingAllowed ||
          isPartiesEventsAllowed ||
          allowsLongTermRentals ||
          allowsTravelResellers) && (
            <>
              <AppDivider marginVertical="12px" borderStyle="dashed" light={false} />
              <CheckoutSectionTitle>Permitted</CheckoutSectionTitle>
              {isPetFriendly ? (
                <HouseRulesItem label={'Pets Allowed'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
              {isWheelchairAccessible ? (
                <HouseRulesItem label={'Wheelchair Accessible'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
              {isSmokingAllowed ? (
                <HouseRulesItem label={'Smoking Allowed'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
              {isPartiesEventsAllowed ? (
                <HouseRulesItem label={'Parties and Events Allowed'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
              {allowsLongTermRentals ? (
                <HouseRulesItem label={'Long Term Rentals Allowed'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
              {allowsTravelResellers ? (
                <HouseRulesItem label={'Travel Resellers Allowed'} value={''} isAllowed isLoading={isLoading} />
              ) : null}
            </>
          )}

        {(!isPetFriendly ||
          !isWheelchairAccessible ||
          !isSmokingAllowed ||
          !isPartiesEventsAllowed ||
          !allowsLongTermRentals ||
          !allowsTravelResellers) && (
            <>
              <AppDivider marginVertical="12px" borderStyle="dashed" light={false} />
              <CheckoutSectionTitle>Not Permitted</CheckoutSectionTitle>
              {!isPetFriendly ? (
                <HouseRulesItem label={'Pets not Allowed'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
              {!isWheelchairAccessible ? (
                <HouseRulesItem label={'Wheelchair Accessible'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
              {!isSmokingAllowed ? (
                <HouseRulesItem label={'Smoking Not Allowed'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
              {!isPartiesEventsAllowed ? (
                <HouseRulesItem label={'No Parties and Events Allowed'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
              {!allowsLongTermRentals ? (
                <HouseRulesItem label={'Long Term Rentals Not Allowed'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
              {!allowsTravelResellers ? (
                <HouseRulesItem label={'Travel Resellers Not Allowed'} value={''} isDisallowed isLoading={isLoading} />
              ) : null}
            </>
          )}
      </Grid>
    </>
  );
};
